import ApiService from '../../../services/ApiService';
import Logger from '../../../services/Logger';
import { getContentLanguage } from '../../../services/LangPrefs';

const GridService = {
    getCountPublished: async (type = 'WORD_SEARCH') => {
        try {
            const params = new URLSearchParams({ type });
            const data = await ApiService.getData(`/home/grid/countPublished?${params.toString()}`);
            return data || { english: { free: 0, paid: 0 }, french: { free: 0, paid: 0 } };
        } catch (error) {
            Logger.error('Error fetching grid count published:', error);
            return { english: { free: 0, paid: 0 }, french: { free: 0, paid: 0 } };
        }
    },

    requestFreePuzzles: async (type = 'WORD_SEARCH', limit = 2, tagOrTags) => {
        try {
            const language = getContentLanguage();
            let url = `/home/grid?free=true&limit=${limit}&type=${type}&sort=publishedAt:desc&language=${language}`;
            const tags = Array.isArray(tagOrTags) ? tagOrTags : (tagOrTags ? [tagOrTags] : []);
            tags.forEach((tag) => { url += `&tags=${encodeURIComponent(tag)}`; });
            const puzzles = await ApiService.getData(url);
            return puzzles;
        } catch (error) {
            Logger.error('Error fetching free puzzles:', error);
            return [];
        }
    },

    /**
     * @param {'all'|'free'|'paid'} [accessFilter] - free = free puzzles only; paid = premium only; all = no filter
     * @param {boolean} [includeLanguage=true] - set false for account library tabs (show all languages)
     */
    requestPlayablePuzzles: async (
        type,
        page = 0,
        pageSize = 10,
        archived,
        isCompleted,
        tagOrTags,
        accessFilter = 'all',
        includeLanguage = true
    ) => {
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                sort: 'publishedAt:desc',
            });
            if (includeLanguage) {
                params.append('language', getContentLanguage());
            }

            // when archived or isCompleted is undefined, don't append to the URL
            if (archived !== undefined) params.append('archived', archived);
            if (isCompleted !== undefined) params.append('isCompleted', isCompleted);

            if (type) params.append('type', type); // only include if defined
            const tags = Array.isArray(tagOrTags) ? tagOrTags : (tagOrTags ? [tagOrTags] : []);
            tags.forEach((tag) => params.append('tags', tag));

            if (accessFilter === 'free') {
                params.append('free', 'true');
            } else if (accessFilter === 'paid') {
                params.append('free', 'false');
            }

            const response = await ApiService.getData(`/home/grid/paging?${params.toString()}`);
            return response;
        } catch (error) {
            Logger.error('Error fetching all puzzles:', error);
            return { content: [], totalElements: 0, totalPages: 0 };
        }
    },


    getPuzzleById: async (id, options = {}) => {
        try {
            const params = new URLSearchParams();
            if (options.isStory) params.set("isStory", "true");
            const qs = params.toString();
            const suffix = qs ? `?${qs}` : "";
            const data = await ApiService.getData(`/home/grid/${id}${suffix}`);

            // Return the raw API data directly
            return data;
        } catch (error) {
            Logger.error('Error fetching puzzle by ID:', error);
            return null;
        }
    },

    startPlay: async (gridId) => {
        try {
            let params = {
                gridId
            };
            const data = await ApiService.postData(`/home/grid-play/start`, params);
            return data;
        } catch (error) {
            Logger.error('Error fetching user play history:', error);
            return null;
        }
    },

    restartPlay: async (gridId) => {
        try {
            let params = {
                gridId,
            };
            const data = await ApiService.postData(`/home/grid-play/restart`, params);
            return data;
        } catch (error) {
            Logger.error('Error fetching user play history:', error);
            return null;
        }
    },

    progressPuzzle: async (_cloudUserId, gridId, wordsFound) => {
        try {
            let params = {
                gridId,
                foundWords: wordsFound  // Map wordsFound to foundWords for API compatibility
            };
            const data = await ApiService.putData(`/home/grid-play/progress`, params);
            return data;
        } catch (error) {
            Logger.error('Error updating puzzle progress:', error);
            return null;
        }
    },

    /**
     * Story puzzles: notify backend when every vocabulary word on the list has been found.
     * @param {string} gridId
     * @param {boolean} [isCompleteStory=true]
     */
    completeStory: async (gridId, isCompleteStory = true) => {
        try {
            const params = { gridId, isCompleteStory };
            const data = await ApiService.putData(`/home/grid-play/complete-story`, params);
            return data;
        } catch (error) {
            Logger.error('Error recording story completion:', error);
            return null;
        }
    },

    /**
     * Record word(s) solved while grid hints were active. Body requires at least one non-empty trimmed string.
     * @param {string} gridId - template / play grid UUID
     * @param {{ word?: string, words?: string[] }} payload
     */
    hintUsed: async (gridId, { word, words } = {}) => {
        try {
            const list = [];
            if (Array.isArray(words)) {
                for (const w of words) {
                    const t = String(w ?? "").trim();
                    if (t) list.push(t);
                }
            }
            if (word != null && String(word).trim()) {
                list.push(String(word).trim());
            }
            if (!list.length) {
                Logger.warn("GridService.hintUsed: no word(s) to send");
                return null;
            }
            const params = { gridId };
            if (list.length === 1) {
                params.word = list[0];
            } else {
                params.words = list;
            }
            const data = await ApiService.putData(`/home/grid-play/hint-used`, params);
            return data;
        } catch (error) {
            Logger.error('Error recording hint use:', error);
            return null;
        }
    },

    /**
     * Record PDF download for analytics. Uses grid template ID.
     * @param {string} gridTplId - Grid template ID (UUID)
     * @param {string} format - 'letter' or 'a4'
     * @param {boolean} [useDisplayClue=true] - Sent as query param when true
     */
    recordDownloadPdf: async (gridTplId, format = 'letter', useDisplayClue = true) => {
        try {
            const params = useDisplayClue ? { format, useDisplayClue: true } : { format };
            await ApiService.getData(`/home/grid-play/${gridTplId}/record-download-pdf`, params);
        } catch (error) {
            Logger.error('Error recording PDF download:', error);
        }
    },

    /**
     * Record download and then fetch/download PDF. Uses pdfUrl if provided, otherwise calls REST API.
     * @param {string} templateId - Template ID (UUID)
     * @param {string} format - 'letter' or 'a4'
     * @param {{ pdfUrl?: string, title?: string, useDisplayClue?: boolean }} options - Optional pre-generated pdfUrl and filename
     */
    recordAndDownloadPdf: async (templateId, format = 'letter', { pdfUrl, title, useDisplayClue = true } = {}) => {
        if (templateId) {
            await GridService.recordDownloadPdf(templateId, format, useDisplayClue);
        }


        const link = document.createElement('a');
        link.download = `${title || 'puzzle'}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        if (pdfUrl) {
            link.href = pdfUrl;
        } else {
            const queryParams = useDisplayClue ? { format, useDisplayClue: true } : { format };
            const blob = await ApiService.downloadFile(`/home/grid-play/${templateId}/download-pdf`, queryParams);
            link.href = window.URL.createObjectURL(blob);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            return;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    /**
     * Download grid puzzle PDF. Uses template ID (grid Tpl ID), not grid play ID.
     * @param {string} templateId - Grid template ID (UUID)
     * @param {string} format - 'letter' or 'a4'
     */
    downloadPuzzle: async (templateId, format = 'letter', useDisplayClue = false) => {
        try {
            const queryParams = useDisplayClue ? { format, useDisplayClue: true } : { format };
            const blob = await ApiService.downloadFile(`/home/grid-play/${templateId}/download-pdf`, queryParams);

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = `puzzle-${templateId}.pdf`; // Set the filename

            // Append to document, click, and cleanup
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the URL to free up memory
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            Logger.error('Error downloading puzzle:', error);
            // Re-throw the error so it can be handled by the caller
            throw error;
        }
    }
};

export default GridService;
